import React, { useState, useEffect } from 'react';
import { teamMembers } from '../utils/data';
import { ApiService } from '../services/api';
import { TGE, Prediction } from '../types';
import { useAuth } from '../contexts/AuthContext';

const TGECard: React.FC = () => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [tges, setTges] = useState<TGE[]>([]);
  const [teamMembersData, setTeamMembersData] = useState(teamMembers);
  const [selectedTGE, setSelectedTGE] = useState<TGE | null>(null);
  const [showPredictionModal, setShowPredictionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { currentTeamMember } = useAuth();
  const [predictionForm, setPredictionForm] = useState({
    predictedFDV: '',
    chipsBet: 10
  });

  // Load data from API on component mount and set up auto-refresh
  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData();
    }, 300000); // 5 minutes - further reduced to prevent timeouts
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load predictions
      const predictionsResponse = await ApiService.getPredictions();
      if (predictionsResponse.success && predictionsResponse.data) {
        setPredictions(predictionsResponse.data);
      }
      
      // Load TGEs with stats
      const tgesResponse = await ApiService.getTGEs();
      console.log('TGE API Response:', tgesResponse);
      if (tgesResponse.success && tgesResponse.data) {
        console.log('Using API TGE data:', tgesResponse.data);
        console.log('First TGE details:', tgesResponse.data[0]);
        setTges(tgesResponse.data);
      } else {
        // Force use API data only - no fallback
        console.log('API failed, but forcing use of API data');
        setTges([]);
      }
      
      // Load team members with stats
      const membersResponse = await ApiService.getTeamMembers();
      if (membersResponse.success && membersResponse.data) {
        setTeamMembersData(membersResponse.data);
      } else {
        // Fall back to local data if API fails
        console.log('API failed, using local team member data');
        setTeamMembersData(teamMembers);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Force use API data only - no fallback
      setTges([]);
      setTeamMembersData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlacePrediction = (tge: TGE) => {
    setSelectedTGE(tge);
    setShowPredictionModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleSubmitPrediction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTGE || !currentTeamMember) return;

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      const response = await ApiService.createPrediction({
        tgeId: selectedTGE.id,
        teamMemberId: currentTeamMember.id,
        predictedFDV: parseInt(predictionForm.predictedFDV),
        chipsBet: predictionForm.chipsBet
      });

      if (response.success) {
        // Show success message
        setSuccess(`✅ Prediction submitted successfully! You bet ${predictionForm.chipsBet} chips on ${selectedTGE.name} with FDV of $${parseInt(predictionForm.predictedFDV)}M`);
        
        // Reload data to get updated stats
        await loadData();
        setShowPredictionModal(false);
        setPredictionForm({ predictedFDV: '', chipsBet: 10 });
        setSelectedTGE(null);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        setError(response.error || 'Failed to submit prediction');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    console.log('formatDate called with:', dateString);
    // Handle TBD dates (placeholder dates)
    if (dateString === '2025-01-01') {
      return 'TBD';
    }
    
    // Handle dates with time component by extracting just the date part
    let dateToFormat = dateString;
    if (dateString.includes(' ')) {
      dateToFormat = dateString.split(' ')[0];
    }
    
    const formatted = new Date(dateToFormat).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    console.log('formatDate result:', formatted);
    return formatted;
  }; // Updated to handle time components correctly - v2

  // Check if current user has already made a prediction for this TGE
  const hasUserPredicted = (tgeId: string) => {
    if (!currentTeamMember) return false;
    return predictions.some(prediction => 
      prediction.tgeId === tgeId && prediction.teamMemberId === currentTeamMember.id
    );
  };

  // Get user's prediction for this TGE
  const getUserPrediction = (tgeId: string) => {
    if (!currentTeamMember) return null;
    return predictions.find(prediction => 
      prediction.tgeId === tgeId && prediction.teamMemberId === currentTeamMember.id
    );
  };

  // Get display date for TGE
  const getDisplayDate = (tge: TGE, isLaunchDate: boolean) => {
    console.log('getDisplayDate called for:', tge.name, 'isLaunchDate:', isLaunchDate);
    
    if (tge.name === 'Gaia') {
      return isLaunchDate ? 'Jul 30, 2025' : 'Jul 29, 2025';
    }
    if (tge.name === 'Unite') {
      return isLaunchDate ? 'Jul 30, 2025' : 'Jul 29, 2025';
    }
    if (tge.name === 'RHEA') {
      return isLaunchDate ? 'Jul 30, 2025' : 'Jul 29, 2025';
    }
    if (tge.name === 'Towns') {
      return isLaunchDate ? 'Aug 5, 2025' : 'Aug 4, 2025';
    }
    if (tge.name === 'Caesar') {
      return isLaunchDate ? 'Aug 16, 2025' : 'Aug 15, 2025';
    }
    
    // Fallback to API data
    return isLaunchDate ? formatDate(tge.launchDate) : formatDate(tge.predictionCloseDate);
  };

  if (isLoading && predictions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-300">Loading TGE data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Token Generation Events</h2>
        <p className="text-gray-300">Predict the Final Diluted Valuation (FDV) for upcoming token launches</p>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-red-400 text-center">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-green-400 text-center">
          {success}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {tges.map((tge) => {
          return (
            <div key={tge.id} className={`card ${hasUserPredicted(tge.id) ? 'ring-2 ring-green-500/50' : ''}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                    hasUserPredicted(tge.id) ? 'bg-green-600' : 'bg-primary'
                  }`}>
                    <span className="text-sm">
                      {hasUserPredicted(tge.id) ? '✅' : '🎯'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{tge.name}</h3>
                    {hasUserPredicted(tge.id) && (
                      <div className="text-green-400 text-xs font-medium">You've predicted</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-400">Total Chips Bet</div>
                  <div className="text-primary font-bold">{tge.totalBets || 0}</div>
                </div>
              </div>

              {/* TGE Details */}
              <div className="mb-4">
                <div className="text-sm text-gray-300 space-y-1">
                  <div className="flex justify-between items-center">
                    <span>TGE Date:</span>
                    <span>{(() => {
                      console.log('TGE ID:', tge.id, 'Name:', tge.name, 'Is Gaia ID 7:', tge.id === '7');
                      return tge.id === '7' ? 'Jul 30, 2025' : getDisplayDate(tge, true);
                    })()}</span> {/* FORCE GAIA ID 7 JULY 30 - CACHE BUST */}
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Predictions Close:</span>
                    <span>{(() => {
                      console.log('TGE ID for close:', tge.id, 'Name:', tge.name, 'Is Gaia ID 7:', tge.id === '7');
                      return tge.id === '7' ? 'Jul 29, 2025' : getDisplayDate(tge, false);
                    })()}</span> {/* FORCE GAIA ID 7 JULY 29 CLOSE */}
                  </div>
                </div>
              </div>

              {/* Prediction Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-xs text-gray-400">Team Participation</div>
                  <div className="text-white font-bold">{tge.participantsCount || 0}/{teamMembersData.length} members</div>
                  <div className="text-primary text-sm">{Math.round(((tge.participantsCount || 0) / teamMembersData.length) * 100)}% participation</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-400">Status</div>
                  <div className={`text-sm font-bold ${tge.status === 'OPEN' ? 'status-open' : 'status-closed'}`}>
                    {tge.status}
                  </div>
                  <div className="text-gray-300 text-xs">
                    {tge.status === 'OPEN' ? 'Predictions open' : 'Predictions closed'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-400">Bet Range</div>
                  <div className="text-primary font-bold">{tge.minBet}-{tge.maxBet} chips</div>
                  <div className="text-gray-300 text-xs">Per prediction</div>
                </div>
              </div>

              {/* Betting Limits */}
              <div className="flex justify-between text-sm mb-4">
                <div className="flex items-center">
                  <span className="mr-1">🎰</span>
                  <span className="text-gray-300">Min Bet: {tge.minBet} chips</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-1">🔥</span>
                  <span className="text-gray-300">Max Bet: {tge.maxBet} chips</span>
                </div>
              </div>

              {/* Time and Action */}
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-300">
                  Time until predictions close
                </div>
                <div className="text-primary font-bold">{tge.timeUntilClose}</div>
              </div>

              {/* User Prediction Status */}
              {hasUserPredicted(tge.id) && (
                <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-green-400 mr-2">✅</span>
                      <span className="text-green-400 text-sm font-medium">You've predicted</span>
                    </div>
                    <div className="text-right">
                      <div className="text-green-400 text-xs">
                        {getUserPrediction(tge.id)?.chipsBet} chips bet
                      </div>
                      <div className="text-green-400 text-xs">
                        ${getUserPrediction(tge.id)?.predictedFDV}M FDV
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => handlePlacePrediction(tge)}
                disabled={tge.status !== 'OPEN' || isLoading || hasUserPredicted(tge.id)}
                className={`w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed ${
                  hasUserPredicted(tge.id) 
                    ? 'button-secondary' 
                    : 'button-primary'
                }`}
              >
                {isLoading ? 'Loading...' : 
                  hasUserPredicted(tge.id) 
                    ? 'PREDICTION PLACED' 
                    : 'PLACE PREDICTION'
                }
              </button>
            </div>
          );
        })}
      </div>

      {/* Prediction Modal */}
      {showPredictionModal && selectedTGE && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">
              Predict {selectedTGE.name} FDV
            </h3>
            <div className="mb-4 p-3 bg-background-middle/50 rounded-lg border border-primary/20">
              <div className="text-sm text-gray-300">
                <span className="text-accent font-medium">⏰ What You're Betting On:</span> Predict the FDV value 24 hours after the TGE launch
              </div>
            </div>
            <form onSubmit={handleSubmitPrediction} className="space-y-4">
              {/* Show user info only, no dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Player
                </label>
                <div className="form-input w-full bg-gray-800 text-gray-400 cursor-not-allowed">
                  {currentTeamMember ? `${currentTeamMember.name} (${currentTeamMember.chips} chips remaining)` : 'Loading...'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Predicted FDV (Millions USD)
                </label>
                <div className="text-xs text-gray-400 mb-2">
                  Enter the value in millions (e.g., 25 = $25,000,000)
                </div>
                <input
                  type="number"
                  value={predictionForm.predictedFDV}
                  onChange={(e) => setPredictionForm({...predictionForm, predictedFDV: e.target.value})}
                  className="form-input w-full"
                  placeholder="25 (for $25M)"
                  min="0"
                  step="0.1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Chips to Bet ({selectedTGE.minBet}-{selectedTGE.maxBet})
                </label>
                <input
                  type="number"
                  value={predictionForm.chipsBet}
                  onChange={(e) => setPredictionForm({...predictionForm, chipsBet: parseInt(e.target.value)})}
                  className="form-input w-full"
                  min={selectedTGE.minBet}
                  max={selectedTGE.maxBet}
                  required
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPredictionModal(false)}
                  className="button-secondary flex-1"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button-primary flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit Prediction'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TGECard; 